import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "@/components/ui/carousel";
import { Clock, MapPin, Scissors, Star, Crown, ArrowRight, Instagram, Phone } from "lucide-react";

export default function Landing() {
  const nav = useNavigate();
  const [settings, setSettings] = useState({});
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/settings"),
      api.get("/services"),
      api.get("/barbers"),
      api.get("/campaigns"),
    ]).then(([s, sv, b, c]) => {
      setSettings(s.data || {});
      setServices(sv.data || []);
      setBarbers(b.data || []);
      setCampaigns(c.data || []);
    });
  }, []);

  const addr = settings.address || {};
  const fullAddr = [addr.street, addr.number].filter(Boolean).join(", ") +
    (addr.district ? ` - ${addr.district}` : "") +
    (addr.city ? `, ${addr.city}` : "") + (addr.state ? `/${addr.state}` : "");

  return (
    <div className="min-h-screen bg-[#09090b]">
      <Navbar logo={settings.logo} shopName={settings.shop_name || "Big Black"} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1503951914875-452162b0f3f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="Barbearia" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-[#09090b]" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28 flex flex-col items-center text-center">
          {settings.logo && (
            <img src={settings.logo} alt="Big Black" className="w-72 sm:w-[28rem] h-auto object-contain animate-fade-up drop-shadow-2xl" data-testid="hero-logo" />
          )}
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-1.5 rounded-full gold-border bg-amber-500/5 animate-fade-up" style={{ animationDelay: "0.1s" }}>
            <Crown className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">Desde 2020</span>
          </div>
          <p className="mt-6 max-w-2xl text-lg text-zinc-300 animate-fade-up" style={{ animationDelay: "0.2s" }}>
            {settings.about || "Estilo, respeito e tradição. Cortes modernos e cuidado masculino de alto padrão."}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            <Button size="lg" className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold text-base rounded-full px-8 h-14 hover:brightness-110" onClick={() => nav("/agendar")} data-testid="hero-book-btn">
              Agendar Horário Agora <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-amber-500/40 text-amber-100 hover:bg-amber-500/10 rounded-full px-8 h-14" onClick={() => document.getElementById("servicos")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-services-btn">
              Ver Serviços
            </Button>
          </div>
        </div>
      </section>

      {/* CAMPAIGNS CAROUSEL */}
      {campaigns.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14" id="promocoes">
          <div className="flex items-center gap-3 mb-6">
            <Star className="w-5 h-5 text-amber-400" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">Promoções & Novidades</h2>
          </div>
          <Carousel opts={{ loop: true }} className="w-full" data-testid="campaign-carousel">
            <CarouselContent>
              {campaigns.map((c) => (
                <CarouselItem key={c.id} className="md:basis-1/2 lg:basis-1/2">
                  <div className="relative rounded-2xl overflow-hidden gold-border group h-64 sm:h-72" data-testid={`campaign-${c.id}`}>
                    {c.image && <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                    <div className="absolute bottom-0 p-6">
                      <h3 className="font-heading text-xl font-bold text-white">{c.title}</h3>
                      <p className="text-sm text-zinc-300 mt-1 max-w-md">{c.description}</p>
                      {c.button_label && (
                        <Button className="mt-4 bg-amber-400 text-black font-bold rounded-full hover:bg-amber-300" onClick={() => c.button_link?.startsWith("/") ? nav(c.button_link) : window.open(c.button_link, "_blank")} data-testid={`campaign-btn-${c.id}`}>
                          {c.button_label}
                        </Button>
                      )}
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2 sm:-left-4 bg-zinc-900/90 border-amber-500/40 text-amber-400 hover:bg-amber-400 hover:text-black h-10 w-10" data-testid="carousel-prev" />
            <CarouselNext className="right-2 sm:-right-4 bg-zinc-900/90 border-amber-500/40 text-amber-400 hover:bg-amber-400 hover:text-black h-10 w-10" data-testid="carousel-next" />
          </Carousel>
          <p className="text-center text-zinc-500 text-xs mt-4 sm:hidden">← arraste para o lado para ver mais →</p>
        </section>
      )}

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14" id="servicos">
        <div className="flex items-center gap-3 mb-2">
          <Scissors className="w-5 h-5 text-amber-400" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">Nossos Serviços</span>
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-8">Escolha seu estilo</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <div key={s.id} className="group rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-amber-500/40 gold-glow transition-colors" data-testid={`service-card-${s.id}`}>
              {s.image && (
                <div className="h-44 overflow-hidden">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-heading text-xl font-bold text-white">{s.name}</h3>
                  <span className="text-amber-400 font-bold whitespace-nowrap">{money(s.price)}</span>
                </div>
                <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{s.description}</p>
                <div className="flex items-center gap-2 mt-4 text-zinc-500 text-sm">
                  <Clock className="w-4 h-4" /> {s.duration} min
                </div>
                <Button className="w-full mt-4 bg-zinc-800 hover:bg-amber-400 hover:text-black text-white font-semibold rounded-full transition-colors" onClick={() => nav(`/agendar?service=${s.id}`)} data-testid={`service-book-${s.id}`}>
                  Agendar este serviço
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BARBERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14" id="barbeiros">
        <div className="flex items-center gap-3 mb-2">
          <Crown className="w-5 h-5 text-amber-400" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">Time</span>
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-8">Nossos Barbeiros</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {barbers.map((b) => (
            <div key={b.id} className="rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-amber-500/40 transition-colors" data-testid={`barber-card-${b.id}`}>
              <div className="h-64 overflow-hidden">
                {b.photo ? <img src={b.photo} alt={b.name} className="w-full h-full object-cover" /> : <div className="w-full h-full pole-stripes opacity-30" />}
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-bold text-white">{b.name}</h3>
                <p className="text-sm text-amber-400 mt-1">{b.role}</p>
                <Button className="w-full mt-4 bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full hover:brightness-110" onClick={() => nav(`/agendar?barber=${b.id}`)} data-testid={`barber-book-${b.id}`}>
                  Agendar com {b.name.split(" ")[0]}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* LOCATION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14" id="localizacao">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-2">
              <MapPin className="w-5 h-5 text-amber-400" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">Onde Estamos</span>
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-4">Venha nos visitar</h2>
            <p className="text-zinc-300">{fullAddr}</p>
            {addr.zip && <p className="text-zinc-500 mt-1">CEP: {addr.zip}</p>}
            <div className="mt-6 space-y-2 text-zinc-400">
              {settings.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-amber-400" /> {settings.phone}</p>}
              {settings.instagram && <p className="flex items-center gap-2"><Instagram className="w-4 h-4 text-amber-400" /> {settings.instagram}</p>}
            </div>
            <Button className="mt-6 bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full hover:brightness-110" onClick={() => window.open(addr.maps_link || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddr)}`, "_blank")} data-testid="location-directions-btn">
              <MapPin className="w-4 h-4 mr-2" /> Como Chegar
            </Button>
          </div>
          <div className="h-72 lg:h-full min-h-[300px]">
            <img src="https://images.unsplash.com/photo-1648221122323-572c13a31663?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Interior" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <footer className="border-t border-amber-500/10 py-10 mt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          {settings.logo && <img src={settings.logo} alt="Big Black" className="h-16 mx-auto mb-4 object-contain" />}
          <p className="text-zinc-500 text-sm">© {new Date().getFullYear()} {settings.shop_name || "Barbearia Big Black"} — Desde 2020. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
